import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

const STEPS = 3;

export default function OnboardingWizard({ onComplete }) {
  const [step, setStep] = useState(1);
  const [journalNote, setJournalNote] = useState('');
  const [saving, setSaving] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  const markComplete = async () => {
    try {
      await api.put('/user/preferences', { preferences: { onboarding_completed: true } });
    } catch (e) {
      console.error('Failed to save onboarding state', e);
    }
    onComplete();
  };

  const handleConnectBroker = () => { markComplete(); navigate('/brokers'); };
  const handleExplore = () => setStep(2);

  const handleJournalSave = async () => {
    if (!journalNote.trim()) { setStep(3); return; }
    setSaving(true);
    try {
      await api.post('/journal', {
        content: journalNote,
        entry_date: new Date().toISOString().split('T')[0],
        mood: null,
        tags: [],
      });
    } catch (e) {
      console.error('Journal save failed', e);
    } finally {
      setSaving(false);
      setStep(3);
    }
  };

  const EXAMPLE_PROMPTS = [
    "What's your main trading goal this month?",
    "Which strategy will you focus on first?",
    "What's one thing you want to measure about your trading?",
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/80 backdrop-blur-sm p-4">
      <div className="bg-slate-800 rounded-2xl shadow-2xl w-full max-w-lg p-8">
        {/* Progress dots */}
        <div className="flex justify-center gap-2 mb-6">
          {[1, 2, 3].map(s => (
            <div
              key={s}
              className={`w-2 h-2 rounded-full transition-colors ${s <= step ? 'bg-emerald-500' : 'bg-slate-600'}`}
            />
          ))}
        </div>

        {/* Step 1: Choose path */}
        {step === 1 && (
          <div className="text-center">
            <div className="text-3xl mb-3">👋</div>
            <h2 className="text-xl font-bold text-white mb-2">
              Welcome{user?.firstName ? `, ${user.firstName}` : ''}!
            </h2>
            <p className="text-slate-400 mb-6">
              Let's get your trading journal set up. How would you like to start?
            </p>
            <div className="space-y-3">
              <button
                onClick={handleConnectBroker}
                className="w-full bg-emerald-600 hover:bg-emerald-500 text-white py-3 px-4 rounded-xl font-medium transition-colors"
              >
                🔗 Connect a broker — sync trades automatically
              </button>
              <button
                onClick={handleExplore}
                className="w-full bg-slate-700 hover:bg-slate-600 text-white py-3 px-4 rounded-xl font-medium transition-colors"
              >
                📓 I'll explore first
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Journal note */}
        {step === 2 && (
          <div>
            <div className="text-3xl text-center mb-3">📝</div>
            <h2 className="text-xl font-bold text-white mb-2 text-center">Write your first journal note</h2>
            <p className="text-slate-400 text-sm mb-4 text-center">
              Journaling is the habit that separates profitable traders.
            </p>
            <div className="flex flex-wrap gap-2 mb-3">
              {EXAMPLE_PROMPTS.map(p => (
                <button
                  key={p}
                  onClick={() => setJournalNote(p)}
                  className="text-xs bg-slate-700 hover:bg-slate-600 text-slate-300 px-3 py-1 rounded-full transition-colors"
                >
                  {p}
                </button>
              ))}
            </div>
            <textarea
              value={journalNote}
              onChange={e => setJournalNote(e.target.value)}
              placeholder="What's on your mind about your trading?"
              className="w-full bg-slate-900 border border-slate-700 rounded-xl p-3 text-slate-200 placeholder-slate-500 resize-none h-28 focus:outline-none focus:border-emerald-500"
            />
            <div className="flex gap-3 mt-4">
              <button
                onClick={() => setStep(3)}
                className="flex-1 bg-slate-700 hover:bg-slate-600 text-slate-300 py-2 rounded-xl transition-colors text-sm"
              >
                Skip
              </button>
              <button
                onClick={handleJournalSave}
                disabled={saving}
                className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white py-2 px-6 rounded-xl transition-colors font-medium disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save & Continue →'}
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Tour complete */}
        {step === 3 && (
          <div className="text-center">
            <div className="text-3xl mb-3">🎯</div>
            <h2 className="text-xl font-bold text-white mb-2">You're all set!</h2>
            <p className="text-slate-400 mb-6">
              Your dashboard is ready. Here are a few shortcuts to know:
            </p>
            <div className="bg-slate-900 rounded-xl p-4 mb-6 text-left space-y-2">
              {[
                ['Shift + J', 'Open Journal'],
                ['Shift + A', 'Open Analytics'],
                ['Shift + B', 'Manage Brokers'],
              ].map(([key, label]) => (
                <div key={key} className="flex items-center justify-between text-sm">
                  <span className="text-slate-400">{label}</span>
                  <kbd className="bg-slate-700 text-slate-300 px-2 py-1 rounded text-xs font-mono">{key}</kbd>
                </div>
              ))}
            </div>
            <button
              onClick={markComplete}
              className="w-full bg-emerald-600 hover:bg-emerald-500 text-white py-3 rounded-xl font-medium transition-colors"
            >
              Go to Dashboard →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
